import Axios, { type AxiosError, type AxiosRequestConfig } from 'axios';

import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';

import { axiosConfig } from './axiosConfig';
import { getTraceId } from './getTraceId';

export const AXIOS_INSTANCE = Axios.create(axiosConfig);
AXIOS_INSTANCE.interceptors.response.use(
  res => {
    // CSV 파일 응답인 경우 에러 체크 건너뛰기
    const contentType = res.headers['content-type'];
    const isCsvResponse =
      typeof contentType === 'string' && contentType.includes('text/csv');

    if (res.status === 200 && !isCsvResponse && res.data.status !== 'SUCCESS') {
      throw new ZepQuizApiError(
        res.data.message || '',
        res.data.status,
        res.status,
        res.data,
      );
    }

    return res;
  },
  (error: AxiosError<{ message?: string; data?: any; status: string }>) => {
    // 네트워크 에러 로깅 (Sentry에는 보고하지 않음)
    if (Axios.isAxiosError(error)) {
      if (!error.response && error.message === 'Network Error') {
        console.warn(
          '[Network Error] 네트워크 연결이 불안정하거나 서버에 접근할 수 없습니다.',
          {
            url: error.config?.url,
            method: error.config?.method,
          },
        );
      } else if (!error.response) {
        console.warn('[Network Error] 서버 응답 없음', {
          url: error.config?.url,
          method: error.config?.method,
          message: error.message,
        });
      } else if (error.code === 'ECONNABORTED') {
        console.warn('[Network Error] 요청이 타임아웃되었습니다.', {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout,
        });
      }
    }

    if (error.response?.data?.status) {
      throw new ZepQuizApiError(
        error.response.data?.message || 'Unknown error',
        error.response.data?.status || 'Error',
        error.response.status,
        error.response.data,
      );
    }
    throw error;
  },
);

AXIOS_INSTANCE.interceptors.request.use(
  config => {
    config.headers['X-ZEP-Trace-Id'] = getTraceId();
    (config as any).metadata = { startTime: new Date() };

    const apiToken = ApiTokenStorage.get();
    if (apiToken != null) {
      config.headers['Authorization'] = `Bearer ${apiToken}`;
    }

    // admin API의 경우 timeout을 3분으로 설정
    if (config.url && config.url.includes('/admin/')) {
      config.timeout = 3 * 60 * 1000; // 3분
    }

    // 파일 업로드 API의 경우 timeout을 30초로 설정
    if (config.url && config.url.includes('/files')) {
      config.timeout = 30 * 1000; // 30초
    }

    if (config.params && config.params.request) {
      const requestParams = config.params.request;

      if (requestParams) {
        config.params = {
          ...config.params,
          ...requestParams,
        };
        delete config.params.request;
      }
      // 빈 문자열 값을 가진 키 삭제
      config.params = Object.fromEntries(
        Object.entries(config.params).filter(([, value]) => value !== ''),
      );
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

export class ZepQuizApiError<T = any> extends Error {
  public details: T;
  public status: string;
  public httpStatus: number;

  constructor(
    message: string,
    status: string,
    httpStatus: number,
    details?: T,
  ) {
    super(message);
    this.name = 'ZepQuizApiError';
    this.details = details || ({} as T);
    this.status = status;
    this.httpStatus = httpStatus;
    this.message = message;
    console.error('ZEP API ERROR :' + JSON.stringify(details), message);
    Object.setPrototypeOf(this, ZepQuizApiError.prototype);
  }
}

// use your own URL here or environment variable

// add a second `options` argument here if you want to pass extra options to each generated query
export const httpsInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): CancellablePromise<T> => {
  const source = Axios.CancelToken.source();
  const promise = AXIOS_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data) as CancellablePromise<T>;

  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};
interface CancellablePromise<T> extends Promise<T> {
  cancel: () => void;
}

export const enum HttpStatusCodeEnum {
  OK_200 = 200,
  CREATED_201 = 201,
  NO_CONTENT_204 = 204,
  BAD_REQUEST_400 = 400,
  UNAUTHORIZED_401 = 401,
  FORBIDDEN_403 = 403,
  NOT_FOUND_404 = 404,
  NOT_ACCEPTABLE_406 = 406,
  CONFLICT_409 = 409,
  PAYLOAD_TOO_LARGE_413 = 413,
  UNSUPPORTED_MEDIA_TYPE_415 = 415,
  UNPROCESSABLE_ENTITY_422 = 422,
  LOCKED_423 = 423,
  TOO_MANY_REQUESTS_429 = 429,
  INTERNAL_SERVER_ERROR_500 = 500,
  BAD_GATEWAY_502 = 502,
  SERVICE_UNAVAILABLE_503 = 503,
  GATEWAY_TIMEOUT_504 = 504,
}

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = ZepQuizApiError<Error>;

export type BodyType<BodyData> = BodyData;
